import { buildComponent } from '#components/build-component'
import { z } from 'zod'

// Component props schema
const ProductMarkerTabsPropsSchema = z.object({
  tabButtonEls: z.custom<NodeListOf<HTMLElement>>(),
  tabContentEls: z.custom<NodeListOf<HTMLElement>>(),
  markerButtonEls: z.custom<NodeListOf<HTMLElement>>(),
  markerContentEls: z.custom<NodeListOf<HTMLElement>>(),
  tabDropdownEl: z.custom<HTMLInputElement | null>()
})

const activate = (el: HTMLElement) => {
  el && el.classList.add('is-active')
}
const reset = (els: NodeListOf<HTMLElement>) => {
  els.length &&
    els.forEach((el) => {
      el.classList.remove('is-active')
    })
}
const handleButtons = (props: z.infer<typeof ProductMarkerTabsPropsSchema>) => {
  const {
    tabButtonEls: buttonEls,
    tabContentEls: contentEls,
    markerButtonEls,
    markerContentEls,
    tabDropdownEl: dropdownEl
  } = props
  const handleClickButton = (buttonEl: HTMLElement) => {
    if (!buttonEl) return

    reset(markerContentEls)

    const buttonTabName = buttonEl.getAttribute('data-tab') || ''
    const targetContentEls = [...contentEls].filter((contentEl) => {
      const contentTabName = contentEl.getAttribute('data-tab')

      return buttonTabName === contentTabName
    })

    if (targetContentEls.length) {
      reset(buttonEls)
      reset(contentEls)
      activate(buttonEl)
      targetContentEls.forEach((targetContentEl) => {
        activate(targetContentEl)
      })
    }

    if (dropdownEl) {
      dropdownEl.value = buttonTabName
    }
  }

  if (dropdownEl) {
    dropdownEl.addEventListener('change', () => {
      const selectedTabName = dropdownEl.value
      const targetButtonEl = [...buttonEls].find((buttonEl) => {
        const tabName = buttonEl.getAttribute('data-tab')

        return tabName === selectedTabName
      })

      targetButtonEl?.click()
    })
  }

  if (buttonEls.length) {
    buttonEls.forEach((buttonEl, index) => {
      if (index === 0) {
        handleClickButton(buttonEl)
      }
      buttonEl.addEventListener('click', () => {
        handleClickButton(buttonEl)
      })
    })
  }

  if (markerButtonEls.length && markerContentEls.length) {
    markerButtonEls.forEach((markerButtonEl) => {
      markerButtonEl.addEventListener('click', () => {
        const isOpened = markerButtonEl.classList.contains('is-active')
        const targetMarkerId = markerButtonEl.getAttribute('data-marker-id')
        const targetMarkerContentEls = [...markerContentEls].filter((markerContentEl) => {
          const markerId = markerContentEl.getAttribute('data-marker-id')

          return markerId === targetMarkerId
        })

        if (targetMarkerContentEls) {
          reset(markerContentEls)
          reset(markerButtonEls)

          if (!isOpened) {
            activate(markerButtonEl)
            targetMarkerContentEls.forEach((targetMarkerContentEl) => {
              activate(targetMarkerContentEl)
            })
          }
        }
      })
    })
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement

      if (!target.closest('.js-product-marker-item')) {
        reset(markerButtonEls)
        reset(markerContentEls)
      }
    })
  }
}

// Register component
const ProductMarkerTabs = buildComponent({
  name: 'product-marker-tabs',
  propsSchema: ProductMarkerTabsPropsSchema,
  onConnected(component) {
    component.props.tabButtonEls = component.querySelectorAll('.js-product-marker-tabs-button')
    component.props.tabContentEls = component.querySelectorAll('.js-product-marker-tabs-content')
    component.props.markerButtonEls = component.querySelectorAll('.js-product-marker-item-button')
    component.props.markerContentEls = component.querySelectorAll('.js-product-marker-item-content')
    component.props.tabDropdownEl = component.querySelector('.js-product-marker-tabs-dropdown')

    handleButtons(component.props)
  }
})

// Declare the element tag name
declare global {
  interface HTMLElementTagNameMap {
    'product-marker-tabs': typeof ProductMarkerTabs
  }
}
